import { Box } from '@factorialco/deprecated-design-system'
import { ExpensesEntitiesExpensableResourceTypeEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import TitleHeader from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/TitleHeader'
import AttachedDocumentsWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/AttachedDocumentsWidget'
import BudgetAndProjectWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/BudgetAndProjectWidget'
import CostCentersWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/CostCentersWidget'
import DescriptionWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/DescriptionWidget'
import ExpenseInformationWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/ExpenseInformationWidget'
import InternalReferenceWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/InternalReferenceWidget'
import PerDiemRatesWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/PerDiemRatesWidget'
import TripInformationWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/TripInformationWidget'
import BasicDetails from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { isOwnExpensable } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

interface ConfirmationScreenProps {
  expensable: Expensable
  customizationFields: CustomizationField[]
  editable: boolean
  children?: React.ReactNode
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
  onEdit: (scrollTo?: string | undefined) => void
}
const ConfirmationScreen = ({
  expensable,
  customizationFields,
  editable,
  children,
  resourceType,
  onEdit,
}: ConfirmationScreenProps) => {
  const { employee: currentEmployee } = useCurrentSession()
  const isDraft = expensable.status === 'draft'
  const isExpensableOwner = isOwnExpensable(expensable, currentEmployee)
  const canReview = expensable.currentUserApprovalStatus === 'pending'
  const showSubmitterText = isDraft && !canReview && isExpensableOwner
  const showReviewerText = canReview && !isExpensableOwner

  const anyTitleHeader = showSubmitterText || showReviewerText

  return (
    <Box gap="s16" paddingY="s32">
      <Box paddingBottom={anyTitleHeader ? 's0' : 's16'}>
        <BasicDetails expensable={expensable} />
      </Box>
      {showSubmitterText && (
        <TitleHeader
          titleLabel={i18n.t('expenses_v2.confirmation_screen.title_submitter')}
          subtitleLabel={i18n.t('expenses_v2.confirmation_screen.subtitle_submitter')}
        />
      )}
      {showReviewerText && (
        <TitleHeader
          titleLabel={i18n.t('expenses_v2.confirmation_screen.title_reviewer')}
          subtitleLabel={i18n.t('expenses_v2.confirmation_screen.subtitle_reviewer')}
        />
      )}
      <ExpenseInformationWidget
        expensable={expensable}
        customizationFields={customizationFields}
        editable={editable}
        onEdit={() => onEdit('basicInformation')}
        isOwnExpensable={isExpensableOwner}
        resourceType={'perdiem'}
      />
      <TripInformationWidget
        expensable={expensable}
        resourceType={resourceType}
        editable={editable}
        onEdit={() => onEdit('tripInformation')}
      />

      <PerDiemRatesWidget
        expensable={expensable}
        onEdit={() => onEdit('perDiemRates')}
        editable={editable}
      />
      <BudgetAndProjectWidget
        expensable={expensable}
        onEdit={() => onEdit('budgetAndProject')}
        editable={editable}
      />
      <InternalReferenceWidget
        expensable={expensable}
        onEdit={() => onEdit('additionalInformation')}
        editable={editable}
      />
      <DescriptionWidget
        expensable={expensable}
        onEdit={() => onEdit('additionalInformation')}
        editable={editable}
      />
      <AttachedDocumentsWidget
        expensable={expensable}
        onEdit={() => onEdit()}
        editable={editable}
      />
      <CostCentersWidget
        expensable={expensable}
        onEdit={() => onEdit('costCenters')}
        editable={editable}
      />
      {children}
    </Box>
  )
}

export default ConfirmationScreen
